// "Lo que entendí de tus notas" (PLAN-NOCHE §2b, criterio 2): el recuadro libre del cuestionario convertido en ajustes
// que el vendedor acepta con UN toque (nunca en silencio): dos medidores, aumento de consumo, cliente nuevo, pocos meses,
// ampliación, titular, consumo que baja y costos adicionales sugeridos. Las reglas viven en compartido/notas.ts (las
// mismas que usa la IA simulada); "No aplica" solo esconde la sugerencia en este navegador.
import { useState } from 'react';
import { Check, Lightbulb, Plus, X } from 'lucide-react';
import { sugerenciasDeNotas } from '@cotizador/compartido';
import type { SugerenciaNotas } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { navegar } from '../../ruteo.tsx';
import { Boton, ErrorEnLinea } from '../../componentes/ui.tsx';
import { usarCotizacion, usarPuedeAqui } from './contexto.tsx';

const llave = (id: string) => `cotizador:notas-descartadas:${id}`;

function leerDescartadas(id: string): string[] {
  try {
    return JSON.parse(window.localStorage.getItem(llave(id)) ?? '[]') as string[];
  } catch {
    return [];
  }
}

function guardarDescartadas(id: string, ids: string[]): void {
  try {
    window.localStorage.setItem(llave(id), JSON.stringify(ids));
  } catch {
    /* sin almacenamiento: se vuelve a enseñar al recargar */
  }
}

function Sugerencia({ s, alDescartar }: { s: SugerenciaNotas; alDescartar: () => void }) {
  const { cot, ejecutar } = usarCotizacion();
  const puede = usarPuedeAqui();
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const aplicar = async () => {
    setOcupado(true);
    setError(null);
    try {
      await ejecutar((c) => {
        // Las ediciones se arman con lo de AHORA (si alguien cambió algo, "antes" ya no coincide y el servidor lo dice).
        const vigente = sugerenciasDeNotas(c.borrador).find((x) => x.id === s.id);
        return api.editarBorrador(c.id, c.revision, vigente?.ediciones.length ? vigente.ediciones : s.ediciones);
      });
    } catch (e) {
      setError(e);
    } finally {
      setOcupado(false);
    }
  };
  return (
    <li className="sugerencia">
      <div className="sugerencia__texto">
        <p className="sugerencia__titulo">{s.titulo}</p>
        <p className="sugerencia__explicacion">{s.explicacion}</p>
        {s.frase && <p className="sugerencia__frase">«{s.frase}»</p>}
        {s.falta && <p className="sugerencia__falta">{s.falta}</p>}
      </div>
      {puede('editar_cotizaciones') && (
        <div className="sugerencia__acciones">
          {s.ediciones.length > 0 && (
            <Boton chico variante="principal" icono={<Check aria-hidden />} cargando={ocupado} onClick={() => void aplicar()}>
              Aplicar
            </Boton>
          )}
          {s.costo && (
            <Boton chico variante="principal" icono={<Plus aria-hidden />} onClick={() => navegar(`/cotizaciones/${cot.id}/costos?agregar=${encodeURIComponent(s.costo!.catalogo_id)}`)}>
              Agregar el costo
            </Boton>
          )}
          {!s.ediciones.length && !s.costo && s.falta && (
            <Boton chico variante="secundario" onClick={() => navegar(`/cotizaciones/${cot.id}/cuestionario/editar`)}>
              Editar el cuestionario
            </Boton>
          )}
          <Boton chico variante="fantasma" icono={<X aria-hidden />} onClick={alDescartar}>
            {s.ediciones.length || s.costo ? 'No aplica' : 'Entendido'}
          </Boton>
        </div>
      )}
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </li>
  );
}

export function SugerenciasNotas() {
  const { cot } = usarCotizacion();
  const [descartadas, setDescartadas] = useState<string[]>(() => leerDescartadas(cot.id));
  const todas = sugerenciasDeNotas(cot.borrador);
  const pendientes = todas.filter((s) => !s.aplicada && !descartadas.includes(s.id));
  const hechas = todas.filter((s) => s.aplicada);
  if (!pendientes.length) return null;
  const descartar = (id: string) => {
    const nuevas = [...descartadas, id];
    setDescartadas(nuevas);
    guardarDescartadas(cot.id, nuevas);
  };
  return (
    <section className="sugerencias" aria-labelledby="sugerencias-titulo">
      <p className="sugerencias__titulo" id="sugerencias-titulo">
        <Lightbulb aria-hidden /> Lo que entendí de tus notas
      </p>
      <ul className="sugerencias__lista">
        {pendientes.map((s) => (
          <Sugerencia key={s.id} s={s} alDescartar={() => descartar(s.id)} />
        ))}
      </ul>
      {hechas.length > 0 && <p className="nota-suave">Ya está: {hechas.map((s) => s.titulo.toLowerCase()).join(' · ')}.</p>}
    </section>
  );
}
